// Story Maker: build a story from word tiles under a constraint, then keep it on the
// bookshelf. Decoy tiles from the wrong part of speech are refused with a grammar hint.

import { h, clear } from '@kit/dom';
import { shuffle, pick } from '@kit/rng';
import type { GameModule, GameContext } from '@shell/types';
import { BANKS, TAG_LABEL, TAG_ROLE, framesForBand, storyBandFor, renderStory, checkConstraints, type Frame, type Tile, type Tag } from '@content/words/story-parts';

const STORIES_PER_ROUND = 3;
const DECOY_TAGS: Tag[] = ['character', 'object', 'verb', 'adjective', 'adverb', 'connector', 'setting', 'reason'];

function mount(host: HTMLElement, ctx: GameContext) {
  let level = ctx.currentLevel();
  const band = storyBandFor(level);
  const frames: Frame[] = shuffle(ctx.rng, framesForBand(band)).slice(0, STORIES_PER_ROUND);
  let fi = 0;
  let frame: Frame = frames[0];
  let picks: (Tile | null)[] = [];
  let filledOnce: boolean[] = [];
  let active = -1;
  let attempts = 0, correct = 0;
  let kept = 0;
  let finishedStory = false;
  const t0 = performance.now();
  let tSlot = performance.now();

  const head = h('div.row', { style: { justifyContent: 'space-between' } }, h('span.muted', null, 'Story ', h('strong', null, '1'), ` of ${frames.length}`), h('span.muted.small', null, ''));
  const goal = h('div.card', { style: { borderLeft: '8px solid var(--words)', fontWeight: '700' } });
  const story = h('div', { style: { fontSize: '19px', lineHeight: '2', padding: '6px 2px' } });
  const tiles = h('div.row.wrap', { style: { gap: '8px', minHeight: '56px' } });
  const tileHint = h('p.muted.small.center', { style: { minHeight: '20px' } });
  const checkBtn = h('button.btn.primary.block', { onclick: check }, 'Check my story') as HTMLButtonElement;
  const prose = h('div.card', { style: { display: 'none', fontSize: '19px', lineHeight: '1.5' } });
  const after = h('div.grid2', { style: { display: 'none' } },
    h('button.btn', { onclick: () => ctx.speak(renderStory(frame, picks)) }, 'Read it'),
    h('button.btn.primary', { onclick: keepIt }, 'Keep it'),
    h('button.btn.ghost', { onclick: nextStory, style: { gridColumn: '1 / -1' } }, 'Next story'),
  );
  host.append(head, goal, story, tiles, tileHint, checkBtn, prose, after);

  function startFrame() {
    frame = frames[fi];
    picks = frame.slots.map(() => null);
    filledOnce = frame.slots.map(() => false);
    active = -1;
    finishedStory = false;
    (head.querySelector('strong') as HTMLElement).textContent = String(fi + 1);
    (head.lastElementChild as HTMLElement).textContent = frame.title;
    goal.textContent = frame.constraints.map(c => c.text).join(' ');
    prose.style.display = 'none'; after.style.display = 'none';
    checkBtn.style.display = '';
    renderStoryLine();
    clear(tiles);
    tileHint.textContent = 'Tap a blank to choose a word for it.';
    // open the first empty slot automatically
    openSlot(0);
  }

  function renderStoryLine() {
    clear(story);
    const parts = frame.template.split(/(\{\d+\})/g);
    for (const part of parts) {
      const m = /^\{(\d+)\}$/.exec(part);
      if (!m) { story.append(document.createTextNode(part)); continue; }
      const i = Number(m[1]);
      const t = picks[i];
      const label = t ? (t.tag === 'skip' ? '·' : t.text) : frame.slots[i].label;
      story.append(h('button', {
        onclick: () => openSlot(i),
        style: { minHeight: '40px', padding: '2px 10px', margin: '2px 1px', borderRadius: '10px', border: `2px solid ${i === active ? 'var(--accent)' : t ? 'var(--line)' : 'var(--words)'}`, background: t ? 'var(--surface)' : 'var(--surface-2)', color: t ? 'var(--text)' : 'var(--words)', fontWeight: '800', fontSize: t ? '18px' : '14px', fontStyle: t ? 'normal' : 'italic', boxShadow: i === active ? '0 0 0 3px var(--accent) inset' : '' },
      }, label));
    }
    checkBtn.disabled = picks.some(p => p === null);
  }

  function openSlot(i: number) {
    if (finishedStory) return;
    active = i;
    renderStoryLine();
    clear(tiles);
    const slot = frame.slots[i];
    const allow = slot.allow;
    const options: Tile[] = [];
    for (const tag of allow) {
      const bank = BANKS[tag];
      const n = tag === 'skip' || tag === 'stop' ? 1 : allow.length === 1 ? 5 : 3;
      options.push(...shuffle(ctx.rng, bank).slice(0, n));
    }
    if (band >= 3) {
      const decoyTag = pick(ctx.rng, DECOY_TAGS.filter(t => !allow.includes(t) && !(t === 'object' && allow.includes('character'))));
      options.push(pick(ctx.rng, BANKS[decoyTag]));
    }
    tileHint.textContent = `Pick ${slot.label === 'nothing' ? 'a word' : slot.label === 'join or stop' ? 'a joining word, or a full stop' : `a word for "${slot.label}"`}.`;
    for (const t of shuffle(ctx.rng, options)) {
      tiles.append(h('button.btn.small', { onclick: () => choose(i, t), style: { fontWeight: '800', textTransform: 'none', fontStyle: t.tag === 'skip' ? 'italic' : 'normal' } }, t.tag === 'skip' ? '(leave it out)' : t.tag === 'stop' ? '. (full stop)' : t.text));
    }
  }

  function choose(i: number, t: Tile) {
    const slot = frame.slots[i];
    const ms = performance.now() - tSlot;
    tSlot = performance.now();
    const id = `story:${band}:${frame.title.slice(0, 10)}:${i}`;
    if (!slot.allow.includes(t.tag)) {
      attempts++;
      ctx.report({ skill: 'grammar', level, correct: false, hintUsed: false, ms, problemId: id });
      ctx.sound('buzz');
      const need = slot.allow.filter(a => a !== 'skip' && a !== 'stop').map(a => TAG_ROLE[a]).join(' or ');
      ctx.say(`"${t.text}" ${TAG_ROLE[t.tag]}. Here we need a word that ${need}.`);
      return;
    }
    if (!filledOnce[i]) {
      filledOnce[i] = true;
      attempts++; correct++;
      ctx.report({ skill: t.tag === 'adjective' || t.tag === 'adverb' ? 'vocabulary' : 'grammar', level, correct: true, hintUsed: false, ms, problemId: id });
    }
    picks[i] = t;
    ctx.sound('pop');
    ctx.say('');
    const nextEmpty = picks.findIndex(p => p === null);
    if (nextEmpty >= 0) openSlot(nextEmpty); else { active = -1; renderStoryLine(); clear(tiles); tileHint.textContent = 'All filled. Check your story, or tap a word to change it.'; }
  }

  function check() {
    if (picks.some(p => p === null)) return;
    const res = checkConstraints(frame, picks);
    if (!res.ok) {
      ctx.sound('buzz');
      const c = res.missing[0];
      ctx.say(`Almost. ${c.text} Tap a blank that could take one.`);
      // highlight slots that could satisfy the first missing constraint
      const wantTag: Tag | null = c.kind === 'countTag' ? c.tag : c.kind === 'lastTag' ? c.tag : 'connector';
      const idx = frame.slots.findIndex((s, i) => s.allow.includes(wantTag) && (picks[i]?.tag !== wantTag));
      if (idx >= 0) openSlot(idx);
      return;
    }
    finishedStory = true;
    active = -1;
    renderStoryLine();
    clear(tiles);
    tileHint.textContent = '';
    checkBtn.style.display = 'none';
    prose.textContent = renderStory(frame, picks);
    prose.style.display = '';
    after.style.display = '';
    ctx.sound('chime');
    ctx.say('A whole story! Read it, keep it, or make another.');
  }

  function keepIt() {
    ctx.keep('story', { title: frame.title, text: renderStory(frame, picks) });
    kept++;
    if (level >= 5) ctx.charm('charm.author');
    nextStory();
  }

  function nextStory() {
    fi++;
    if (fi >= frames.length) {
      ctx.end({ attempts, correct, perfect: attempts === correct, durationMs: performance.now() - t0, notes: kept ? [`Stories kept: ${kept}`] : [] });
      return;
    }
    level = ctx.currentLevel();
    startFrame();
  }

  startFrame();
  return { destroy() { clear(host); } };
}

const mod: GameModule = {
  id: 'words.story-maker',
  title: 'Story Maker',
  blurb: 'Build a story from word tiles. Keep the good ones on your bookshelf.',
  category: 'words',
  skills: ['grammar', 'vocabulary'],
  levelRange: [1, 10],
  unlock: { stars: 6 },
  mount,
};
export default mod;

export { TAG_LABEL };
